import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, plural, ASSET_ICONS, isITRole } from '../utils';

export default function Dashboard() {
  const { user, withAuth } = useAuth();
  const role = user?.role;
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentAssets, setRecentAssets] = useState([]);
  const [terminations, setTerminations] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const isIT = isITRole(role);
      try {
        await withAuth(async () => {
          // One round trip's worth of waiting instead of four. This ran the two
          // status counts and both role-gated reports sequentially, and asked
          // for the unfiltered total separately — which the recent-assets call
          // already reports, since every list response carries `total`.
          //
          // The two reports swallow their own failures: neither is the point of
          // the page, and letting one reject would take the stats and the
          // recent list down with it.
          const [recent, unassignedRes, activeRes, repairRes, termRes, licRes] = await Promise.all([
            api.getAssets({ limit: 5 }),
            api.getUnassignedAssets(),
            api.getAssets({ status: 'active', limit: 1 }),
            api.getAssets({ status: 'in_repair', limit: 1 }),
            isIT || role === 'hr' ? api.getTerminationReport().catch(() => null) : null,
            isIT ? api.getExpiringLicenses({ days: 60 }).catch(() => null) : null,
          ]);

          setStats({
            total: recent.total || 0,
            active: activeRes.total || 0,
            unassigned: unassignedRes.data?.length || 0,
            inRepair: repairRes.total || 0,
          });
          setRecentAssets(recent.data || []);
          if (termRes) setTerminations(termRes.data?.slice(0, 5) || []);
          if (licRes) setExpiringLicenses(licRes.data?.slice(0, 5) || []);
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [withAuth, role]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600 }}>Dashboard</h1>
        <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>
          Welcome back, {user?.displayName?.split(' ')[0]}
        </p>
      </div>

      <div style={{ padding: 28 }}>
        {/* Stats */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 28 }}>
            {[
              { label: 'Total Assets', value: stats.total, color: 'var(--text-0)' },
              { label: 'Active', value: stats.active, color: 'var(--green)' },
              { label: 'Unassigned', value: stats.unassigned, color: 'var(--yellow)' },
              { label: 'In Repair', value: stats.inRepair, color: 'var(--red)' },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Recent Assets */}
          <div className="card">
            <div className="card-header">
              <h3>Recent Assets</h3>
              <button className="btn btn-sm" onClick={() => navigate('/assets')}>View All</button>
            </div>
            {recentAssets.length === 0
              ? <div className="empty-state">No assets found</div>
              : recentAssets.map(a => (
                <div
                  key={a.id}
                  onClick={() => navigate(`/assets/${a.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 20px', borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 20 }}>{ASSET_ICONS[a.asset_type]}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.display_name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {a.asset_tag} · {a.location_name || '—'}
                    </div>
                  </div>
                  <span className={`badge ${a.status === 'active' ? 'badge-green' : a.status === 'in_repair' ? 'badge-yellow' : 'badge-gray'}`}>
                    {a.status?.replace(/_/g, ' ')}
                  </span>
                </div>
              ))
            }
          </div>

          {/* Termination Alerts */}
          <div className="card">
            <div className="card-header">
              <h3>Pending Returns</h3>
              <button className="btn btn-sm" onClick={() => navigate('/reports')}>View All</button>
            </div>
            {terminations.length === 0
              ? <div className="empty-state">No pending returns</div>
              : terminations.map(t => (
                <div
                  key={t.flag_id}
                  onClick={() => navigate(`/employees/${t.employee_id || ''}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 20px', borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--red-dim)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 14,
                  }}>⚠</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{t.employee_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {t.assets_outstanding} {plural(t.assets_outstanding, 'device')} outstanding · {t.home_location}
                    </div>
                  </div>
                  <span className="badge badge-red">{t.assets_outstanding} due</span>
                </div>
              ))
            }
          </div>

          {/* Expiring Licenses */}
          {expiringLicenses.length > 0 && (
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <h3>Licenses Expiring Soon</h3>
                <button className="btn btn-sm" onClick={() => navigate('/reports')}>View All</button>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Vendor</th>
                    <th>Bound To</th>
                    <th>Expires</th>
                    <th>Days Left</th>
                  </tr>
                </thead>
                <tbody>
                  {expiringLicenses.map(l => (
                    <tr key={l.asset_tag}>
                      <td style={{ fontWeight: 500 }}>{l.product_name}</td>
                      <td style={{ color: 'var(--text-2)' }}>{l.vendor || '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{l.bound_device_name || '—'}</td>
                      <td>{formatDate(l.expiry_date)}</td>
                      <td>
                        <span className={`badge ${l.days_remaining <= 30 ? 'badge-red' : 'badge-yellow'}`}>
                          {l.days_remaining}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
